// @flow

import type { MessageDescriptor } from 'react-intl';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { useAppFlags } from '../../utils/featureFlags';
import { fetchPropertyCoraStatus, fetchPropertyLeslieStatus } from './data';
import messages from './messages';
import type { AIAssistantsViewModel, AssistantStatus } from './types';

type AssistantStatusQuery = {
  queryName: string,
  flagEnabled: boolean,
  fetchStatus: ({ selectedProperty: Object }) => Promise<Object>,
  isEnabled: (Object) => boolean,
  failedToLoadMessage: MessageDescriptor,
};

/**
 * One assistant's on/off status for the selected property.
 *
 * `status: undefined` is the "do not render this assistant at all" answer, and it covers both
 * cases that must look the same: the assistant's feature flag being off, and the status not
 * having arrived yet. The assistant's own on/off state is separate from whether it is listed -
 * a property where it is configured off still lists it, showing Off.
 */
function useAssistantStatus({
  queryName,
  flagEnabled,
  fetchStatus,
  isEnabled,
  failedToLoadMessage,
}: AssistantStatusQuery): {
  status: AssistantStatus | void,
  isLoading: boolean,
} {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();

  // Both ids are part of the request path, so both belong in the cache key: without them a
  // switch of selected property would be served the previous property's status. `enabled`
  // waits for the property for the same reason - the URL cannot be built without it.
  const { data, isLoading, isError } = useQuery(
    [queryName, selectedProperty?.organizationId, selectedProperty?.id],
    () => fetchStatus({ selectedProperty }),
    {
      enabled: Boolean(flagEnabled && selectedProperty?.id),
      // A forbidden read never becomes an allowed one, so retrying only triples the load on a
      // route some users cannot read. Failing on the first response is also what lets the page
      // settle on its blank state instead of sitting in react-query's retry-loading window.
      retry: false,
      // Reading, not writing - an "update failed" message would claim a change was attempted.
      //
      // A forbidden read is not an error to report. The tab is deliberately shown to every
      // user, and the roles that lack the read scope will open it; for them the status is
      // simply not knowable, which the area's blank state already expresses. Toasting would
      // report a failure on a page they were invited to visit and can do nothing about.
      onError: (error) => {
        if (error?.statusCode === 403) return;
        toastr.error(
          intl.formatMessage(messages.errorHeader),
          intl.formatMessage(failedToLoadMessage),
        );
      },
    },
  );

  return {
    status: flagEnabled && data ? { enabled: isEnabled(data) } : undefined,
    // A read that has failed is done, not loading - it must not hold a spinner that never
    // clears. On error the page has no status and no card, which is exactly the blank state.
    isLoading: Boolean(
      flagEnabled && selectedProperty?.id && isLoading && !isError,
    ),
  };
}

/**
 * What the AI Assistants area shows for the selected property.
 *
 * With no assistant to show the page is deliberately blank rather than carrying an empty
 * state, so WalkMe owns the "coming soon" message instead of the app.
 */
export function useAIAssistants(): AIAssistantsViewModel {
  const intl = useIntl();
  const { leslie: leslieFlagEnabled, coraV1: coraFlagEnabled } = useAppFlags();

  const leslie = useAssistantStatus({
    queryName: 'propertyLeslieStatus',
    flagEnabled: Boolean(leslieFlagEnabled),
    fetchStatus: fetchPropertyLeslieStatus,
    isEnabled: (data) => data.enabled === true,
    failedToLoadMessage: messages.failedToLoadStatus,
  });

  const cora = useAssistantStatus({
    queryName: 'propertyCoraStatus',
    flagEnabled: Boolean(coraFlagEnabled),
    fetchStatus: fetchPropertyCoraStatus,
    isEnabled: (data) => data.isEnabled === true,
    failedToLoadMessage: messages.failedToLoadCoraStatus,
  });

  return {
    title: intl.formatMessage(messages.title),
    isLoading: leslie.isLoading || cora.isLoading,
    leslie: leslie.status,
    cora: cora.status,
  };
}
